import { Dispatch } from "react";
import {
  fetchContactsApi,
  fetchActionEventsApi,
  fetchMediaHistoryApi,
  fetchContactAssigneesApi,
  fetchModuleSettingsApi,
} from "../../client/api.client";
import { WhatsAppAction, IWhatsAppContact } from "./WhatsApp.types";

/**
 * Factory: creates all action functions bound to a dispatch.
 * Usage: const actions = useMemo(() => createWhatsAppActions(dispatch), [dispatch]);
 */
export const createWhatsAppActions = (dispatch: Dispatch<WhatsAppAction>) => ({
  fetchContacts: async (
    companyOutletUuid: string,
    whatsappWebService: boolean,
    pageNumber?: number | null,
    rowsInPerPage?: number | null,
    date?: { fromDate: string; toDate: string },
    recipientNumber?: string,
    filters?: string[],
  ) => {
    try {
      dispatch({ type: "CONTACTS_LOADING" });

      const result = await fetchContactsApi({
        companyOutletUuid,
        whatsappWebService,
        pageNumber,
        rowsInPerPage,
        date,
        recipientNumber,
        filters,
      });

      dispatch({
        type: "CONTACTS_SUCCESS",
        payload: result,
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      if (msg === "Whatsapp is not scanned") {
        dispatch({ type: "CONTACTS_NOT_SCANNED" });
      } else if (msg === "Whatsapp Service OFF") {
        dispatch({ type: "CONTACTS_SERVICE_OFF" });
      } else {
        dispatch({ type: "CONTACTS_FAILED", payload: { error: msg || "Failed to fetch contacts" } });
      }
    }
  },

  clearContacts: () => dispatch({ type: "CONTACTS_CLEAR" }),

  setContacts: (contacts: IWhatsAppContact[]) =>
    dispatch({ type: "CONTACTS_SET", payload: { contacts } }),

  setActiveChat: (contact: IWhatsAppContact | null) =>
    dispatch({ type: "SET_ACTIVE_CHAT", payload: contact }),

  fetchActionEvents: async (conversationId: string, page = 1, perPage = 10) => {
    try {
      dispatch({ type: "EVENTS_LOADING" });
      const result = await fetchActionEventsApi(conversationId, page, perPage);
      dispatch({ type: "EVENTS_SUCCESS", payload: result });
    } catch (err: any) {
      dispatch({
        type: "EVENTS_FAILED",
        payload: { error: err?.response?.data?.message || "Failed to fetch events" },
      });
    }
  },

  clearEvents: () => dispatch({ type: "EVENTS_CLEAR" }),

  fetchMediaHistory: async (
    phoneNumber: string,
    companyOutletUuid: string,
    whatsappWebService: boolean,
    page = 1,
    perPage = 10,
  ) => {
    try {
      dispatch({ type: "MEDIA_LOADING" });
      const result = await fetchMediaHistoryApi(phoneNumber, companyOutletUuid, whatsappWebService, page, perPage);
      dispatch({ type: "MEDIA_SUCCESS", payload: result });
    } catch (err: any) {
      dispatch({
        type: "MEDIA_FAILED",
        payload: { error: err?.response?.data?.message || "Failed to fetch media" },
      });
    }
  },

  fetchContactAssignees: async (
    companyOutletUuid: string,
    columnName: string,
    columnValue: string,
    moduleName: string,
  ) => {
    try {
      dispatch({ type: "ASSIGNEES_LOADING" });
      const result = await fetchContactAssigneesApi(companyOutletUuid, columnName, columnValue, moduleName);
      dispatch({ type: "ASSIGNEES_SUCCESS", payload: { data: result.data } });
    } catch (err: any) {
      dispatch({
        type: "ASSIGNEES_FAILED",
        payload: { error: err?.response?.data?.message || "Failed to fetch assignees" },
      });
    }
  },

  fetchModuleSettings: async (companyOutletUuid: string, moduleName: string) => {
    try {
      dispatch({ type: "MODULE_SETTINGS_LOADING" });
      const result = await fetchModuleSettingsApi(companyOutletUuid, moduleName);
      dispatch({ type: "MODULE_SETTINGS_SUCCESS", payload: { data: result.data } });
    } catch (err: any) {
      dispatch({ type: "MODULE_SETTINGS_FAILED" });
    }
  },
});
